import _ from 'lodash';
import { Reducer } from 'redux';
import { TThunkAction } from '.';
import { HierarchySourceType, IHierarchyMetadata, IHrisFileListItem } from '../models';
import { TFetchableEntity } from '../models/IFetchableEntity';
import * as HierarchyAPI from '../services/HierarchyAPI';
import {
	IDistributionAutoSelectHierarchyTopNodes,
	IHierarchyGetDisconnectedHierarchyListFailed,
	IHierarchyGetDisconnectedHierarchyListRequested,
	IHierarchyGetDisconnectedHierarchyListSucceeded,
	IHierarchyGetHrisHierarchyFailed,
	IHierarchyGetHrisHierarchyRequested,
	IHierarchyGetHrisHierarchySucceeded,
	IHierarchyGetHrisListFailed,
	IHierarchyGetHrisListRequested,
	IHierarchyGetHrisListSucceeded,
	IHierarchyGetListFailed,
	IHierarchyGetListRequested,
	IHierarchyGetListSucceeded,
} from './Actions';
import { ActionTypes } from './ActionTypes';

export interface IExternalHierarchyState {
	hierarchyMetadatas: TFetchableEntity<IHierarchyMetadata[]>;
	hrisFiles: TFetchableEntity<IHrisFileListItem[]>;
}

export const defaultHierarchyMetadata: IHierarchyMetadata = {
	hierarchyNodeId: '',
	hierarchyId: '',
	fileName: '',
	name: '',
	hierarchyType: 0,
	clientId: 0,
	rootHierarchyNodeId: '',
	hierarchyState: 0,
	hrisId: '',
	version: 0,
	modifiedBy: '',
	createdBy: '',
	hierarchyDefinition: undefined,
	sourceType: HierarchySourceType.Hierarchy,
	createdOn: new Date(),
	modifiedOn: new Date(),
	structureOnlyHierarchy: false,
};

export const defaultState: IExternalHierarchyState = {
	hierarchyMetadatas: { isLoading: true },
	hrisFiles: { isLoading: true },
};

export type KnownActions =
	| IHierarchyGetHrisListRequested
	| IHierarchyGetHrisListSucceeded
	| IHierarchyGetHrisListFailed
	| IHierarchyGetHrisHierarchyRequested
	| IHierarchyGetHrisHierarchyFailed
	| IHierarchyGetHrisHierarchySucceeded
	| IHierarchyGetDisconnectedHierarchyListFailed
	| IHierarchyGetDisconnectedHierarchyListRequested
	| IHierarchyGetDisconnectedHierarchyListSucceeded
	| IHierarchyGetListRequested
	| IHierarchyGetListSucceeded
	| IHierarchyGetListFailed
	| IDistributionAutoSelectHierarchyTopNodes;

type TActionCreator<R> = TThunkAction<R, KnownActions>;

export const ActionCreators = {
	GetDisconnectedHierarchyForClient: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.HIERARCHY_GET_DISCONNECTED_HIERARCHY_REQUESTED });

		return HierarchyAPI.getDisconnectedHierarchiesListForClient()
			.then((hierarchies: IHierarchyMetadata[]) => {
				dispatch({ type: ActionTypes.HIERARCHY_GET_DISCONNECTED_HIERARCHY_SUCCEEDED, payload: hierarchies });
			})
			.catch(() => {
				dispatch({ type: ActionTypes.HIERARCHY_GET_DISCONNECTED_HIERARCHY_FAILED });
			});
	},
	GetHierarchies:
		(structureOnlyHierarchy = true) =>
		(dispatch: any) => {
			dispatch({ type: ActionTypes.HIERARCHY_GET_LIST_REQUESTED });

			return HierarchyAPI.getHierarchies(structureOnlyHierarchy)
				.then((hierarchyList: IHierarchyMetadata[]) => {
					dispatch({ type: ActionTypes.HIERARCHY_GET_LIST_SUCCEEDED, payload: hierarchyList });
				})
				.catch(() => {
					dispatch({ type: ActionTypes.HIERARCHY_GET_LIST_FAILED });
				});
		},
	GetHrisListForClient: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.HIERARCHY_GET_HRIS_LIST_REQUESTED });

		return HierarchyAPI.getHrisListForClient()
			.then((hierarchyList: IHrisFileListItem[]) => {
				dispatch({ type: ActionTypes.HIERARCHY_GET_HRIS_LIST_SUCCEEDED, payload: hierarchyList });
			})
			.catch(() => {
				dispatch({ type: ActionTypes.HIERARCHY_GET_HRIS_LIST_FAILED });
			});
	},
	GetHrisHierarchies:
		(hrisId: string): TActionCreator<void> =>
		(dispatch) => {
			dispatch({ type: ActionTypes.HIERARCHY_GET_HRIS_HIERARCHY_REQUESTED });

			return HierarchyAPI.getHrisHierarchies(hrisId)
				.then((hrisHierarchy: IHierarchyMetadata[]) => {
					dispatch({ type: ActionTypes.HIERARCHY_GET_HRIS_HIERARCHY_SUCCEEDED, payload: hrisHierarchy });
				})
				.catch(() => {
					dispatch({ type: ActionTypes.HIERARCHY_GET_HRIS_HIERARCHY_FAILED });
				});
		},
};

export const reducer: Reducer<IExternalHierarchyState, KnownActions> = (
	state: IExternalHierarchyState | undefined,
	action: KnownActions,
): IExternalHierarchyState => {
	if (!state) {
		return defaultState;
	}

	switch (action.type) {
		case ActionTypes.HIERARCHY_GET_HRIS_LIST_REQUESTED:
			return { ...state, hrisFiles: { isLoading: true } };
		case ActionTypes.HIERARCHY_GET_HRIS_LIST_SUCCEEDED: {
			return { ...state, hrisFiles: action.payload };
		}
		case ActionTypes.HIERARCHY_GET_HRIS_LIST_FAILED: {
			return {
				...state,
				hrisFiles: {
					isLoading: false,
					couldNotFetchData: true,
				},
			};
		}
		case ActionTypes.HIERARCHY_GET_LIST_REQUESTED:
		case ActionTypes.HIERARCHY_GET_HRIS_HIERARCHY_REQUESTED:
		case ActionTypes.HIERARCHY_GET_DISCONNECTED_HIERARCHY_REQUESTED:
			return { ...state, hierarchyMetadatas: { isLoading: true } };
		case ActionTypes.HIERARCHY_GET_LIST_SUCCEEDED:
		case ActionTypes.HIERARCHY_GET_HRIS_HIERARCHY_SUCCEEDED:
		case ActionTypes.HIERARCHY_GET_DISCONNECTED_HIERARCHY_SUCCEEDED: {
			return { ...state, hierarchyMetadatas: action.payload };
		}
		case ActionTypes.HIERARCHY_GET_LIST_FAILED:
		case ActionTypes.HIERARCHY_GET_HRIS_HIERARCHY_FAILED:
		case ActionTypes.HIERARCHY_GET_DISCONNECTED_HIERARCHY_FAILED: {
			return {
				...state,
				hierarchyMetadatas: {
					isLoading: false,
					couldNotFetchData: true,
				},
			};
		}
		default: {
			return state;
		}
	}
};
